/*
 * Order-minimum enforcement for the wholesale cart — replaces the MinCart Order
 * Limits app, which validated quantity strictly per cart line and could not tell
 * our deliberately-quantity-1 tester line (Stage 1) apart from a real
 * under-minimum order.
 *
 * Rule 1 — per-product minimum quantity: for every product in the cart, its real
 *   order quantity — summed across all of its lines, EXCLUDING any line carrying
 *   the `Tester` property — must be 0 or at least settings.min_order_quantity.
 *   Products in settings.min_qty_excluded_collections are exempt from this rule
 *   (flagged per row by data-excluded-from-min-qty in main-cart-items.liquid),
 *   but still count toward Rule 2.
 * Rule 2 — cart total: cart.total_price must be at least settings.min_cart_total.
 *
 * On any violation the checkout control is disabled and inline messages are
 * shown next to it; when all rules pass it is re-enabled and the messages are
 * cleared. Recomputed on load and on every `cartUpdate` (same subscription
 * pattern as assets/tester-line.js). Both checkout entry points are gated: the
 * full cart page (#checkout / form#cart) and the cart drawer
 * (#CartDrawer-Checkout / form#CartDrawer-Form).
 *
 * Validation is done entirely against /cart.js. No native min/required
 * constraint validation on inputs — that silently swallows the submit event,
 * which would also break Stage 3's checkout intercept. The checkout control is
 * gated directly via document-level capture-phase click / submit handlers.
 *
 * Config comes from data-* on #order-minimums (see sections/main-cart-footer.liquid).
 * This file does not reference or touch the MinCart app embed.
 */
(function () {
  if (window.__orderMinimumsInit) return;

  const config = document.getElementById('order-minimums');
  if (!config) return;
  window.__orderMinimumsInit = true;

  const TESTER_PROPERTY_KEY = 'Tester';
  const MIN_QUANTITY = parseInt(config.dataset.minQuantity, 10) || 0;
  const MIN_TOTAL_CENTS = parseInt(config.dataset.minTotalCents, 10) || 0;
  const CURRENCY_SYMBOL = config.dataset.currencySymbol || '$';
  const MSG_QTY =
    config.dataset.msgQty || 'Add [count] more of [product] to reach the [minimum]-unit minimum.';
  const MSG_TOTAL =
    config.dataset.msgTotal || 'Add [amount] more to reach the [minimum] order minimum.';

  // Fail open: if the JS never runs / errors, checkout is not stranded.
  let blocked = false;

  const isTesterLine = (item) =>
    Boolean(item.properties && item.properties[TESTER_PROPERTY_KEY]);

  const getCart = () => fetch(`${routes.cart_url}.js`).then((response) => response.json());

  const money = (cents) => `${CURRENCY_SYMBOL}${(Math.max(0, cents) / 100).toFixed(2)}`;

  const escapeHtml = (value) =>
    String(value).replace(
      /[&<>"']/g,
      (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch])
    );

  // Product ids exempt from Rule 1, read from the rendered cart rows. Recomputed
  // on every call so it stays correct after each AJAX section re-render.
  const excludedProductIds = () => {
    const ids = new Set();
    document.querySelectorAll('.cart-item[data-product-id]').forEach((row) => {
      if (row.dataset.excludedFromMinQty === 'true') {
        ids.add(String(row.dataset.productId));
      }
    });
    return ids;
  };

  const computeViolations = (cart) => {
    const messages = [];
    const excluded = excludedProductIds();

    // Rule 1 — per-product minimum quantity, aggregated across all of a product's
    // lines and variants, tester lines excluded.
    const byProduct = new Map();
    cart.items.forEach((item) => {
      if (isTesterLine(item)) return;
      const productId = String(item.product_id);
      if (excluded.has(productId)) return;
      const entry = byProduct.get(productId) || { quantity: 0, title: item.product_title };
      entry.quantity += item.quantity;
      byProduct.set(productId, entry);
    });
    byProduct.forEach(({ quantity, title }) => {
      if (quantity > 0 && quantity < MIN_QUANTITY) {
        // Substitute [product] last so a product title can't shadow another token.
        messages.push(
          MSG_QTY.replace('[count]', String(MIN_QUANTITY - quantity))
            .replace('[minimum]', String(MIN_QUANTITY))
            .replace('[product]', title)
        );
      }
    });

    // Rule 2 — cart total (includes every line, tester lines and
    // excluded-collection products alike).
    if (cart.total_price < MIN_TOTAL_CENTS) {
      messages.push(
        MSG_TOTAL.replace('[amount]', money(MIN_TOTAL_CENTS - cart.total_price)).replace(
          '[minimum]',
          money(MIN_TOTAL_CENTS)
        )
      );
    }

    return messages;
  };

  // The full-cart-page checkout button and the cart drawer's. Only one is
  // present at a time (settings.cart_type), so gate whichever exists.
  const checkoutControls = () =>
    ['checkout', 'CartDrawer-Checkout']
      .map((id) => document.getElementById(id))
      .filter(Boolean);

  const applyState = (messages) => {
    const controls = checkoutControls();
    blocked = messages.length > 0;

    if (!blocked) {
      config.hidden = true;
      config.innerHTML = '';
      controls.forEach((control) => {
        control.disabled = false;
        control.removeAttribute('aria-disabled');
      });
      return;
    }

    config.hidden = false;
    config.innerHTML =
      '<ul class="order-minimums__list">' +
      messages.map((message) => `<li>${escapeHtml(message)}</li>`).join('') +
      '</ul>';
    controls.forEach((control) => {
      control.disabled = true;
      control.setAttribute('aria-disabled', 'true');
    });
  };

  const validate = async () => {
    let cart;
    try {
      cart = await getCart();
    } catch (error) {
      console.error('[order-minimums] could not read /cart.js', error);
      return;
    }

    if (!cart || !Array.isArray(cart.items) || cart.item_count === 0) {
      // Empty cart: leave the checkout control as the theme rendered it.
      config.hidden = true;
      config.innerHTML = '';
      blocked = false;
      return;
    }

    applyState(computeViolations(cart));
  };

  // Gate the checkout action while any rule is violated, for BOTH the full cart
  // page (#checkout / form#cart) and the cart drawer (#CartDrawer-Checkout /
  // form#CartDrawer-Form). Document-level capture listeners survive cart section
  // re-renders and run before a later Stage 3 handler; when not blocked they are
  // a no-op and let the event through.
  document.addEventListener(
    'click',
    (event) => {
      if (!blocked) return;
      const control =
        event.target &&
        event.target.closest &&
        event.target.closest('#checkout, #CartDrawer-Checkout');
      if (!control) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      config.hidden = false;
      config.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (!blocked) return;
      if (event.target && (event.target.id === 'cart' || event.target.id === 'CartDrawer-Form')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  subscribe(PUB_SUB_EVENTS.cartUpdate, validate);
  validate();
})();
